import { useMemo, useState } from "react";

type CopyTarget = "public" | "embed" | "iframe" | null;

function buildPublicHousingHtsUrls() {
  const publicUrl = new URL("/housing-hts/", window.location.origin).toString();
  const embed = new URL("/housing-hts/", window.location.origin);
  embed.searchParams.set("embed", "1");
  const embedUrl = embed.toString();
  const iframeCode = `<iframe src="${embedUrl}" width="100%" height="850" loading="lazy" style="border:0;" title="아파트 실거래 HTS"></iframe>`;
  return { publicUrl, embedUrl, iframeCode };
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("clipboard copy failed");
}

export default function PublicHousingHtsShareTools({ compact = false }: { compact?: boolean }) {
  const { publicUrl, embedUrl, iframeCode } = useMemo(buildPublicHousingHtsUrls, []);
  const [copied, setCopied] = useState<CopyTarget>(null);
  const [copyError, setCopyError] = useState(false);

  const handleCopy = async (target: Exclude<CopyTarget, null>, value: string) => {
    try {
      await copyText(value);
      setCopied(target);
      setCopyError(false);
      window.setTimeout(() => setCopied((current) => current === target ? null : current), 1600);
    } catch {
      setCopyError(true);
      setCopied(null);
    }
  };

  return (
    <section className={`public-housing-hts-share ${compact ? "is-compact" : ""}`} data-export-hide="true" aria-label="공개 HTS 공유 도구">
      <div className="public-housing-hts-share__head">
        <div>
          <strong>공유 · 삽입</strong>
          <span>공개 페이지 URL과 게시판 iframe용 주소·HTML 코드를 바로 복사합니다.</span>
        </div>
        <a href={publicUrl} target="_blank" rel="noreferrer">공개 화면 열기 ↗</a>
      </div>
      <div className="public-housing-hts-share__rows">
        <div className="public-housing-hts-share__row">
          <span>공개 URL</span>
          <code>{publicUrl}</code>
          <button type="button" onClick={() => void handleCopy("public", publicUrl)}>{copied === "public" ? "복사됨" : "복사"}</button>
        </div>
        <div className="public-housing-hts-share__row">
          <span>iframe URL</span>
          <code>{embedUrl}</code>
          <button type="button" onClick={() => void handleCopy("embed", embedUrl)}>{copied === "embed" ? "복사됨" : "복사"}</button>
        </div>
        <div className="public-housing-hts-share__row public-housing-hts-share__row--code">
          <span>iframe HTML</span>
          <code>{iframeCode}</code>
          <button type="button" onClick={() => void handleCopy("iframe", iframeCode)}>{copied === "iframe" ? "복사됨" : "코드 복사"}</button>
        </div>
      </div>
      {copyError && <small className="public-housing-hts-share__error">자동 복사에 실패했습니다. 위 주소/코드를 직접 선택해 복사할 수 있습니다.</small>}
    </section>
  );
}
